import { useCallback, useEffect, useRef } from 'react';
import { useVoiceStore } from '@/stores/useVoiceStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface UseSpokenResponseOptions {
  getToken?: () => Promise<string | null>;
}

function stripMarkdownForSpeech(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[\[(?:IMG:)?\d+\]\]/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_#>~-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitSpeakableSegments(buffer: string, force: boolean): { segments: string[]; rest: string } {
  const normalized = buffer.replace(/\s+/g, ' ').trimStart();
  if (!normalized) {
    return { segments: [], rest: '' };
  }

  let boundaryIndex = -1;
  for (const match of normalized.matchAll(/[.!?](?=\s|$)/g)) {
    boundaryIndex = (match.index ?? -1) + 1;
  }

  if (boundaryIndex >= 48) {
    return {
      segments: [normalized.slice(0, boundaryIndex).trim()],
      rest: normalized.slice(boundaryIndex).trimStart(),
    };
  }

  if (!force && normalized.length > 260) {
    const splitAt = normalized.lastIndexOf(' ', 220);
    const index = splitAt > 80 ? splitAt : 220;
    return {
      segments: [normalized.slice(0, index).trim()],
      rest: normalized.slice(index).trimStart(),
    };
  }

  if (force) {
    return { segments: [normalized.trim()], rest: '' };
  }

  return { segments: [], rest: normalized };
}

export function useSpokenResponse(options: UseSpokenResponseOptions = {}) {
  const { getToken } = options;
  const enabled = useVoiceStore((state) => state.audioOutputEnabled);
  const setEnabled = useVoiceStore((state) => state.setAudioOutputEnabled);
  const ttsAvailable = useVoiceStore((state) => state.ttsAvailable);
  const ttsVoiceId = useVoiceStore((state) => state.ttsVoiceId);
  const volume = useVoiceStore((state) => state.volume);
  const workspaceId = useWorkspaceStore((state) => state.activeWorkspace?.id ?? '');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const queueRef = useRef<string[]>([]);
  const playingRef = useRef(false);
  const streamKeyRef = useRef<string | null>(null);
  const suppressedStreamKeyRef = useRef<string | null>(null);
  const lastSpeechTextRef = useRef('');
  const pendingSpeechRef = useRef('');
  const playbackGenerationRef = useRef(0);

  const stop = useCallback(() => {
    playbackGenerationRef.current += 1;
    audioRef.current?.pause();
    audioRef.current = null;
    queueRef.current = [];
    playingRef.current = false;
    streamKeyRef.current = null;
    lastSpeechTextRef.current = '';
    pendingSpeechRef.current = '';
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const cleanupCurrentAudio = useCallback(() => {
    audioRef.current = null;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const playNext = useCallback(async () => {
    if (playingRef.current || queueRef.current.length === 0) {
      return;
    }

    const speechText = queueRef.current.shift();
    if (!enabled || !ttsAvailable || !speechText) {
      return;
    }

    playingRef.current = true;
    const playbackGeneration = playbackGenerationRef.current;

    const isPlaybackStillCurrent = () => (
      playbackGenerationRef.current === playbackGeneration
        && useVoiceStore.getState().audioOutputEnabled
        && useVoiceStore.getState().ttsAvailable
    );

    const token = getToken ? await getToken().catch(() => null) : null;
    if (!isPlaybackStillCurrent()) {
      playingRef.current = false;
      return;
    }

    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch('/api/python/voice/tts', {
      method: 'POST',
      headers,
      body: JSON.stringify({ text: speechText, voice_id: ttsVoiceId, workspace_id: workspaceId }),
    });
    if (!isPlaybackStillCurrent()) {
      playingRef.current = false;
      return;
    }

    if (!response.ok) {
      suppressedStreamKeyRef.current = streamKeyRef.current;
      stop();
      playingRef.current = false;
      return;
    }

    const blob = await response.blob();
    if (!isPlaybackStillCurrent()) {
      playingRef.current = false;
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const audio = new Audio(objectUrl);
    audio.volume = volume;
    audioRef.current = audio;
    objectUrlRef.current = objectUrl;
    audio.onended = () => {
      if (!isPlaybackStillCurrent()) {
        cleanupCurrentAudio();
        playingRef.current = false;
        return;
      }
      cleanupCurrentAudio();
      playingRef.current = false;
      void playNext();
    };
    await audio.play().catch(() => {
      cleanupCurrentAudio();
      playingRef.current = false;
      void playNext();
    });
  }, [cleanupCurrentAudio, enabled, getToken, stop, ttsAvailable, ttsVoiceId, volume, workspaceId]);

  const enqueueSpeech = useCallback((segments: string[]) => {
    const speakable = segments.map((segment) => segment.trim()).filter(Boolean);
    if (speakable.length === 0) {
      return;
    }

    queueRef.current.push(...speakable);
    void playNext();
  }, [playNext]);

  const speak = useCallback(async (text: string) => {
    const speechText = stripMarkdownForSpeech(text);
    if (!enabled || !ttsAvailable || !speechText) {
      return;
    }

    stop();
    suppressedStreamKeyRef.current = null;
    streamKeyRef.current = 'single';
    enqueueSpeech([speechText]);
  }, [enabled, enqueueSpeech, stop, ttsAvailable]);

  const speakProgressively = useCallback((streamKey: string, text: string, isComplete: boolean) => {
    if (!enabled || !ttsAvailable) {
      stop();
      return;
    }

    if (suppressedStreamKeyRef.current === streamKey) {
      return;
    }

    if (streamKeyRef.current !== streamKey) {
      stop();
      suppressedStreamKeyRef.current = null;
      streamKeyRef.current = streamKey;
    }

    const speechText = stripMarkdownForSpeech(text);
    const previous = lastSpeechTextRef.current;
    const delta = speechText.startsWith(previous)
      ? speechText.slice(previous.length)
      : speechText;
    lastSpeechTextRef.current = speechText;

    if (delta.trim()) {
      pendingSpeechRef.current = `${pendingSpeechRef.current} ${delta}`.trim();
    }

    while (pendingSpeechRef.current) {
      const { segments, rest } = splitSpeakableSegments(pendingSpeechRef.current, isComplete);
      if (segments.length === 0) {
        pendingSpeechRef.current = rest;
        break;
      }
      pendingSpeechRef.current = rest;
      enqueueSpeech(segments);
      if (!isComplete) {
        break;
      }
    }
  }, [enabled, enqueueSpeech, stop, ttsAvailable]);

  const toggleEnabled = useCallback(() => {
    if (!ttsAvailable) {
      setEnabled(false);
      return;
    }

    const next = !enabled;
    setEnabled(next);
    if (!next) {
      stop();
    }
  }, [enabled, setEnabled, stop, ttsAvailable]);

  useEffect(() => stop, [stop]);

  useEffect(() => {
    if (!enabled) {
      stop();
    }
  }, [enabled, stop]);

  return {
    enabled,
    available: ttsAvailable,
    speak,
    speakProgressively,
    stop,
    toggleEnabled,
  };
}